import { useEffect } from "react";

const BASE_URL = "https://rankaro.io";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
}

const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = `${BASE_URL}/favicon.png`,
  ogType = "website",
  noIndex = false,
}: SEOHeadProps) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (el) {
        el.setAttribute("content", content);
      } else {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("content", content);
        document.head.appendChild(el);
      }
      return el as HTMLMetaElement;
    };

    const setLink = (rel: string, href: string, extraAttr?: string, extraVal?: string) => {
      const selector = extraAttr
        ? `link[rel="${rel}"][${extraAttr}="${extraVal}"]`
        : `link[rel="${rel}"]`;
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute("href", href);
      } else {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (extraAttr && extraVal) el.setAttribute(extraAttr, extraVal);
        el.setAttribute("href", href);
        document.head.appendChild(el);
      }
      return el as HTMLLinkElement;
    };

    // Meta description
    setMeta("name", "description", description);

    // Robots
    if (noIndex) {
      setMeta("name", "robots", "noindex, nofollow");
    } else {
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) robotsEl.remove();
    }

    // Canonical
    const canonicalUrl = canonical || `${BASE_URL}${window.location.pathname}`;
    setLink("canonical", canonicalUrl);

    // Open Graph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:image:width", "512");
    setMeta("property", "og:image:height", "512");
    setMeta("property", "og:site_name", "Rankaro");
    setMeta("property", "og:locale", "es_ES");

    // Twitter
    setMeta("name", "twitter:card", "summary");
    setMeta("name", "twitter:site", "@rankaro_io");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    // Hreflang
    const path = window.location.pathname;
    const hrefEs = setLink("alternate", `${BASE_URL}${path}`, "hreflang", "es");
    const hrefEn = setLink("alternate", `${BASE_URL}${path}`, "hreflang", "en");
    const hrefDefault = setLink("alternate", `${BASE_URL}${path}`, "hreflang", "x-default");

    return () => {
      document.title = prevTitle;
      // Clean hreflang on unmount
      hrefEs.remove();
      hrefEn.remove();
      hrefDefault.remove();
      // Remove robots noindex if it was set
      if (noIndex) {
        const robotsEl = document.querySelector('meta[name="robots"]');
        if (robotsEl) robotsEl.remove();
      }
    };
  }, [title, description, canonical, ogImage, ogType, noIndex]);

  return null;
};

export default SEOHead;
